import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  NOTISTACK_FAILURE_OPTIONS,
  NOTISTACK_SUCCESS_OPTIONS,
} from "constants/index";
import { User } from "features/auth/auth";
import { MANAGE_INTEGRATIONS_PERMISSION } from "features/auth/permissions";
import {
  IntegrationField,
  SecretOption,
  useGetIntegrationsQuery,
  useUpdateIntegrationsMutation,
} from "features/settings/integrations";
import { useAuth } from "features/store";

export const NOT_SET_PLACEHOLDER = "Not set";

/** Settings > Integrations: which secret each integration reads. */
const Integrations = () => {
  const { enqueueSnackbar } = useSnackbar();
  // AuthState.user is still `any`; SettingsPage states the shape the same way.
  const user: User | null | undefined = useAuth().user;
  // The API refuses a write without it; the pickers are read-only to match.
  const canManage = (user?.permissions ?? []).includes(
    MANAGE_INTEGRATIONS_PERMISSION,
  );
  const { data, isLoading } = useGetIntegrationsQuery();
  const [updateIntegrations, { isLoading: isSaving }] =
    useUpdateIntegrationsMutation();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }

  // A failed refetch keeps showing the last good answer.
  if (!data) {
    return <Alert severity="error">Failed to load the integrations.</Alert>;
  }

  const handleChange = async (
    field: IntegrationField,
    secret: SecretOption | null,
  ) => {
    try {
      await updateIntegrations({ [field.key]: secret?.id ?? null }).unwrap();
      enqueueSnackbar(`${field.label} saved.`, NOTISTACK_SUCCESS_OPTIONS);
    } catch {
      enqueueSnackbar(
        `Failed to save ${field.label}.`,
        NOTISTACK_FAILURE_OPTIONS,
      );
    }
  };

  return (
    <Stack spacing={2}>
      {!canManage && (
        <Typography variant="body2" color="text.secondary">
          You can view these settings but not change them.
        </Typography>
      )}
      {data.fields.map((field) => (
        <Stack
          key={field.key}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={{ xs: 1, sm: 2 }}
        >
          <Typography
            id={`integration-${field.key}-label`}
            sx={{ width: { sm: 200 }, flexShrink: 0 }}
          >
            {field.label}
          </Typography>
          <Autocomplete
            sx={{ flexGrow: 1 }}
            options={data.secret_options}
            value={field.secret}
            onChange={(_event, secret) => handleChange(field, secret)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={!canManage || isSaving}
            // A long name wraps in the list rather than clipping.
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.id}
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={NOT_SET_PLACEHOLDER}
                inputProps={{
                  ...params.inputProps,
                  "aria-labelledby": `integration-${field.key}-label`,
                  // The field is wide enough for most names; hover shows a
                  // longer one in full.
                  title: field.secret?.name,
                }}
              />
            )}
          />
        </Stack>
      ))}
    </Stack>
  );
};

export default Integrations;
